// NATAMI — Punch list: walk-through items before handover, grouped by area.
//
// Server: src/server/punchListRoutes.ts (owner-scoped). Pure logic shared with
// the server: src/shared/punchListLogic.ts + src/shared/punchListTemplate.ts —
// the UI never re-derives status stamps; it sends the new status and renders
// what the server returns.
//
// Errors: every fetch checks r.ok and maps the server's CODE to fixed bilingual
// copy (punchErrorCopy) — raw codes / server text are never rendered.
// Deletes confirm INLINE (two-step button), never with a browser dialog.
// Demo projects keep their items in local state and never call the API.

import { apiFetch, apiUrl } from '../utils/api'
import { readAccessMode } from '../hooks/useSubscriptionPlan'
import { formatDateOnly, dateOnlyKey, toDateInputValue } from '../utils/dateOnly'
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ListChecks, Plus, Download, Pencil, Trash2, X, Camera, AlertTriangle, ClipboardList } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import {
  PUNCH_AREAS,
  PUNCH_AREA_LABELS,
  PUNCH_TEMPLATE,
  PUNCH_TRADES,
  displayPunchDescription,
  displayPunchTrade,
  type PunchArea,
} from '../shared/punchListTemplate'
import {
  PUNCH_STATUSES,
  PUNCH_STATUS_LABELS,
  groupPunchItemsByArea,
  isPunchClosed,
  isPunchStatus,
  nextPunchStatus,
  punchProgress,
  type PunchStatus,
} from '../shared/punchListLogic'

// ── TYPES ─────────────────────────────────────────

interface PunchItem {
  id: string
  area: string
  trade?: string | null
  description: string
  templateKey?: string | null
  responsibleContactId?: string | null
  responsibleName?: string | null
  responsibleContact?: { id: string; name: string; company?: string | null } | null
  status: string
  dueDate?: string | null
  photoPath?: string | null
  notes?: string | null
  sortOrder: number
  completedAt?: string | null
  verifiedAt?: string | null
}

interface Contact { id: string; name: string; company?: string | null; trade?: string | null }
interface Photo { id: string; storagePath: string; thumbnailPath?: string | null; description?: string | null }

interface ItemPayload {
  area: PunchArea
  description: string
  trade: string | null
  responsibleContactId: string | null
  responsibleName: string | null
  status: PunchStatus
  dueDate: string | null
  notes: string | null
  progressPhotoId?: string | null
}

// ── COPY ──────────────────────────────────────────

const STATUS_STYLES: Record<PunchStatus, { bg: string; text: string; border: string }> = {
  OPEN: { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
  IN_PROGRESS: { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
  DONE: { bg: '#E0F7FA', text: '#00796B', border: '#B2EBF2' },
  VERIFIED: { bg: '#ECFDF5', text: '#15803D', border: '#BBF7D0' },
}

/** Server code → fixed bilingual copy. Unknown codes fall back to the generic line. */
export function punchErrorCopy(code: string, isEs: boolean): string {
  const map: Record<string, [string, string]> = {
    load: ['No se pudo cargar el punch list. Intenta de nuevo.', 'Could not load the punch list. Please try again.'],
    save: ['No se pudo guardar el pendiente. Intenta de nuevo.', 'Could not save the item. Please try again.'],
    delete: ['No se pudo eliminar el pendiente. Intenta de nuevo.', 'Could not delete the item. Please try again.'],
    seed: ['No se pudo cargar la plantilla. Intenta de nuevo.', 'Could not load the template. Please try again.'],
    pdf: ['No se pudo generar el PDF. Intenta de nuevo.', 'Could not generate the PDF. Please try again.'],
    punch_items_exist: ['Este proyecto ya tiene pendientes: la plantilla solo se carga en una lista vacía.', 'This project already has items: the template only loads into an empty list.'],
    contact_not_found: ['Ese contacto ya no existe en el proyecto. Elige otro.', 'That contact is no longer in this project. Pick another one.'],
    photo_not_found: ['Esa foto ya no existe en el proyecto.', 'That photo is no longer in this project.'],
    invalid_due_date: ['La fecha límite no es válida.', 'The due date is not valid.'],
    invalid_input: ['Revisa los datos: falta la descripción o hay un campo inválido.', 'Check the fields: the description is missing or a value is invalid.'],
    not_found: ['Ese pendiente ya no existe. Recarga la lista.', 'That item no longer exists. Reload the list.'],
    description_required: ['Escribe una descripción.', 'Enter a description.'],
    // The server is the enforcer even when the screen could not read the role/plan
    // (fail-open UI): its refusal must still read as a clear sentence, never "something went wrong".
    read_only: ['Tu cuenta está en solo lectura. Reactiva tu plan para agregar o editar pendientes.', 'Your account is read-only. Reactivate your plan to add or edit items.'],
    team_role_forbidden: ['Tu rol en este equipo es de solo lectura: no puedes editar pendientes.', 'Your role on this team is read-only: you cannot edit items.'],
  }
  const pair = map[code] ?? ['Algo salió mal. Intenta de nuevo.', 'Something went wrong. Please try again.']
  return isEs ? pair[0] : pair[1]
}

/** Read the server's error CODE (never its text) from a failed response. */
async function failureCode(r: Response, fallback: string): Promise<string> {
  try {
    const body = await r.json()
    return typeof body?.error === 'string' && /^[a-z_]+$/.test(body.error) ? body.error : fallback
  } catch {
    return fallback
  }
}

// ── DEMO DATA ─────────────────────────────────────

// Only ever called behind isDemoProject(projectId) (see useState initializer).
function demoItems(): PunchItem[] {
  const seed: Array<[string, PunchStatus, string | null, string | null]> = [
    ['kit-cab-doors', 'DONE', null, null],
    ['bath-tub-caulk', 'IN_PROGRESS', 'Azulejos García', null],
    ['liv-drywall', 'OPEN', 'Mendez Drywall', '2026-11-05T12:00:00.000Z'],
    ['ele-smoke', 'VERIFIED', null, null],
    ['lan-grading', 'OPEN', null, '2026-11-12T12:00:00.000Z'],
    ['oth-clean', 'OPEN', null, null],
  ]
  return seed.map(([key, status, responsibleName, dueDate], i) => {
    const t = PUNCH_TEMPLATE.find((x) => x.key === key)!
    return { id: `demo-punch-${i + 1}`, area: t.area, trade: t.trade, description: t.description_es, templateKey: t.key, responsibleName, status, dueDate, sortOrder: i }
  })
}

function localTodayKey(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

// ── COMPONENT ─────────────────────────────────────

export default function PunchListModule({ authTokenOverride }: { authTokenOverride?: string | null } = {}) {
  const { getToken: clerkGetToken } = useAuth()
  // Tests de componente (sin Clerk real) pasan authTokenOverride, igual que PermitsModule.
  const getToken = authTokenOverride !== undefined ? async () => authTokenOverride : clerkGetToken
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language.startsWith('es')
  const lang: 'es' | 'en' = isEs ? 'es' : 'en'
  const isDemo = isDemoProject(projectId)

  const [demoState, setDemoState] = useState<PunchItem[]>(() => (isDemo ? demoItems() : []))
  const [statusFilter, setStatusFilter] = useState<'ALL' | PunchStatus>('ALL')
  const [areaFilter, setAreaFilter] = useState<'ALL' | PunchArea>('ALL')
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [photoToken, setPhotoToken] = useState<string | null>(null)

  const authHeaders = async (json = true): Promise<Record<string, string>> => {
    const token = await getToken()
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { 'Content-Type': 'application/json' } : {}) }
  }

  useEffect(() => {
    if (isDemo) return
    let alive = true
    getToken().then((t) => { if (alive) setPhotoToken(t) }).catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkGetToken, authTokenOverride, isDemo])

  // Codex #544 [P1]: a VIEWER, or an account in read-only (no paid plan), must not
  // see actions the server will refuse. The server stays the enforcer; if these
  // reads fail the buttons stay visible and the server answers as before.
  const writeAccess = useQuery({
    queryKey: ['punch-write-access'],
    enabled: !isDemo,
    retry: false,
    queryFn: async () => {
      const h = await authHeaders(false)
      const [sub, me] = await Promise.all([
        apiFetch('/api/subscription', { headers: h }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        apiFetch('/api/team/me', { headers: h }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ])
      return { accessMode: readAccessMode(sub).accessMode, role: (me?.membership?.role ?? null) as string | null }
    },
  })
  const readOnlyReason: 'billing' | 'viewer' | null = writeAccess.data?.accessMode === 'read_only'
    ? 'billing'
    : writeAccess.data?.role === 'VIEWER' ? 'viewer' : null
  const canWrite = readOnlyReason === null

  const itemsQuery = useQuery<PunchItem[]>({
    queryKey: ['punch-items', projectId],
    queryFn: async () => {
      const r = await apiFetch(`/api/projects/${projectId}/punch-items`, { headers: await authHeaders(false) })
      if (!r.ok) throw new Error(await failureCode(r, 'load'))
      const body = await r.json()
      return Array.isArray(body) ? body : []
    },
    enabled: !isDemo && !!projectId,
    retry: false,
  })

  const contactsQuery = useQuery<Contact[]>({
    queryKey: ['contacts', projectId, 'punch'],
    queryFn: async () => {
      const r = await apiFetch(`/api/projects/${projectId}/contacts`, { headers: await authHeaders(false) })
      if (!r.ok) throw new Error('load')
      const body = await r.json()
      return Array.isArray(body) ? body : []
    },
    enabled: !isDemo && !!projectId,
    retry: false,
  })

  const items: PunchItem[] = isDemo ? demoState : (itemsQuery.data ?? [])
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['punch-items', projectId] })
  const fail = (err: unknown, fallback: string) => setActionError(err instanceof Error ? err.message : fallback)

  // ── mutations ──
  const createMutation = useMutation({
    mutationFn: async (payload: ItemPayload) => {
      if (isDemo) {
        setDemoState((prev) => [...prev, { id: `demo-punch-${Date.now()}`, ...payload, sortOrder: prev.length }])
        return
      }
      const r = await apiFetch(`/api/projects/${projectId}/punch-items`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(payload) })
      if (!r.ok) throw new Error(await failureCode(r, 'save'))
    },
    onSuccess: () => { setShowAdd(false); setActionError(null); if (!isDemo) invalidate() },
    onError: (e) => fail(e, 'save'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<ItemPayload> }) => {
      if (isDemo) {
        setDemoState((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)))
        return
      }
      const r = await apiFetch(`/api/projects/${projectId}/punch-items/${id}`, { method: 'PATCH', headers: await authHeaders(), body: JSON.stringify(patch) })
      if (!r.ok) throw new Error(await failureCode(r, 'save'))
    },
    onSuccess: () => { setEditingId(null); setActionError(null); if (!isDemo) invalidate() },
    onError: (e) => fail(e, 'save'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        setDemoState((prev) => prev.filter((i) => i.id !== id))
        return
      }
      const r = await apiFetch(`/api/projects/${projectId}/punch-items/${id}`, { method: 'DELETE', headers: await authHeaders(false) })
      if (!r.ok) throw new Error(await failureCode(r, 'delete'))
    },
    onSuccess: () => { setEditingId(null); setActionError(null); if (!isDemo) invalidate() },
    onError: (e) => fail(e, 'delete'),
  })

  const seedMutation = useMutation({
    mutationFn: async () => {
      if (isDemo) {
        setDemoState(PUNCH_TEMPLATE.map((t, i) => ({ id: `demo-tpl-${i}`, area: t.area, trade: t.trade, description: isEs ? t.description_es : t.description_en, templateKey: t.key, status: 'OPEN', sortOrder: i })))
        return
      }
      const r = await apiFetch(`/api/projects/${projectId}/punch-items/seed-template`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ lang }) })
      if (!r.ok) throw new Error(await failureCode(r, 'seed'))
    },
    onSuccess: () => { setActionError(null); if (!isDemo) invalidate() },
    onError: (e) => fail(e, 'seed'),
  })

  const downloadPdf = async () => {
    if (isDemo) return
    setPdfBusy(true)
    setActionError(null)
    try {
      const r = await apiFetch(`/api/projects/${projectId}/punch-items/pdf?lang=${lang}`, { headers: await authHeaders(false) })
      if (!r.ok) throw new Error(await failureCode(r, 'pdf'))
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `punch-list-${projectId}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      fail(e, 'pdf')
    } finally {
      setPdfBusy(false)
    }
  }

  // ── derived ──
  const progress = punchProgress(items)
  const filtered = items.filter((i) => (statusFilter === 'ALL' || i.status === statusFilter) && (areaFilter === 'ALL' || i.area === areaFilter))
  const groups = groupPunchItemsByArea(filtered)
  const areasPresent = useMemo(() => PUNCH_AREAS.filter((a) => items.some((i) => i.area === a)), [items])
  const todayKey = localTodayKey()
  const contacts = contactsQuery.data ?? []
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || seedMutation.isPending

  const responsibleOf = (i: PunchItem) =>
    i.responsibleContact?.name ? (i.responsibleContact.company ? `${i.responsibleContact.name} (${i.responsibleContact.company})` : i.responsibleContact.name) : (i.responsibleName || '')

  return (
    <div className="p-4 sm:p-5 max-w-4xl mx-auto space-y-4 w-full min-w-0" data-testid="punch-list-module">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <ListChecks size={20} className="text-[#06539B] shrink-0" /> Punch list
          </h2>
          <p className="text-[12px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? `${progress.total} en total · ${progress.total - progress.closed} abiertos · ${progress.closed} terminados`
              : `${progress.total} total · ${progress.total - progress.closed} open · ${progress.closed} done`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {items.length > 0 && (
            <button
              type="button"
              onClick={downloadPdf}
              disabled={isDemo || pdfBusy}
              title={isDemo ? (isEs ? 'Disponible en proyectos guardados' : 'Available on saved projects') : undefined}
              className="min-h-[44px] flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 rounded-full hover:bg-[#F2F0EB] disabled:opacity-50"
              data-testid="punch-download-pdf"
            >
              <Download size={14} />
              {pdfBusy ? (isEs ? 'Generando…' : 'Generating…') : (isEs ? 'Descargar PDF' : 'Download PDF')}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => { setShowAdd(true); setEditingId(null) }}
              className="min-h-[44px] flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 rounded-full"
              data-testid="punch-add"
            >
              <Plus size={14} />
              {isEs ? 'Agregar' : 'Add item'}
            </button>
          )}
        </div>
      </div>

      {readOnlyReason && (
        <p className="text-[12px] text-[#6B665F] bg-[#FAFAF8] border border-[#ECEAE4] rounded-xl px-3 py-2" data-testid="punch-readonly-notice">
          {readOnlyReason === 'viewer'
            ? (isEs ? 'Tienes acceso de solo lectura en este equipo: puedes ver y descargar el PDF, pero no editar.' : 'You have read-only access on this team: you can view and download the PDF, but not edit.')
            : (isEs ? 'Tu cuenta está en solo lectura. Reactiva tu plan para agregar o editar pendientes; el PDF sigue disponible.' : 'Your account is read-only. Reactivate your plan to add or edit items; the PDF is still available.')}
        </p>
      )}

      {isDemo && (
        <p className="text-[12px] text-[#9E9890] bg-[#FAFAF8] border border-dashed border-[#ECEAE4] rounded-xl px-3 py-2">
          {isEs ? 'Proyecto demo: los cambios no se guardan y el PDF está disponible en proyectos guardados.' : 'Demo project: changes are not saved and the PDF is available on saved projects.'}
        </p>
      )}

      {actionError && (
        <div role="alert" data-testid="punch-error" className="flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] text-[13px] font-[600] rounded-xl px-3 py-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span className="flex-1 min-w-0 break-words">{punchErrorCopy(actionError, isEs)}</span>
          <button type="button" onClick={() => setActionError(null)} aria-label={isEs ? 'Cerrar' : 'Dismiss'} className="min-w-[44px] min-h-[44px] -my-2 -mr-2 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Progress */}
      {items.length > 0 && (
        <div className="bg-white border border-[#ECEAE4] rounded-2xl p-4">
          <div className="flex items-center justify-between text-[12px] font-[800] text-[#1C1A17]">
            <span>{isEs ? 'Avance' : 'Progress'}</span>
            <span data-testid="punch-progress">{progress.percent}%</span>
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-[#F2F0EB] overflow-hidden">
            <div className="h-full rounded-full bg-[#00BCD4]" style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
      )}

      {showAdd && canWrite && (
        <ItemForm
          isEs={isEs}
          lang={lang}
          contacts={contacts}
          contactsFailed={contactsQuery.isError}
          projectId={projectId}
          isDemo={isDemo}
          photoToken={photoToken}
          busy={createMutation.isPending}
          onCancel={() => setShowAdd(false)}
          onSubmit={(payload) => createMutation.mutate(payload)}
          onValidationError={(code) => setActionError(code)}
        />
      )}

      {/* Loading / load error */}
      {!isDemo && itemsQuery.isLoading && (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((k) => <div key={k} className="h-16 rounded-xl bg-[#F2F0EB] animate-pulse" />)}
        </div>
      )}
      {!isDemo && itemsQuery.isError && (
        <div role="alert" data-testid="punch-load-error" className="bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] text-[13px] font-[600] rounded-xl px-3 py-3 flex flex-wrap items-center gap-2">
          <span className="flex-1 min-w-0">{punchErrorCopy('load', isEs)}</span>
          <button type="button" onClick={() => itemsQuery.refetch()} className="min-h-[44px] px-4 rounded-full border border-[#FECACA] font-[800]">
            {isEs ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {/* Empty state */}
      {(isDemo || itemsQuery.isSuccess) && items.length === 0 && !showAdd && (
        <div className="border-2 border-dashed border-[#ECEAE4] rounded-2xl p-6 text-center bg-[#FAFAF8]" data-testid="punch-empty">
          <ClipboardList size={28} className="mx-auto text-[#9E9890]" />
          <p className="mt-2 text-[14px] font-[800] text-[#1C1A17]">{isEs ? 'Aún no hay pendientes' : 'No punch items yet'}</p>
          <p className="mt-1 text-[12px] text-[#9E9890]">
            {isEs
              ? `Carga la plantilla estándar NATAMI (${PUNCH_TEMPLATE.length} revisiones por área) o agrega pendientes uno a uno.`
              : `Load the NATAMI standard template (${PUNCH_TEMPLATE.length} checks by area) or add items one by one.`}
          </p>
          {canWrite && <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
              className="min-h-[44px] px-5 rounded-full bg-[#06539B] text-white text-[13px] font-[800] disabled:opacity-60"
              data-testid="punch-seed-template"
            >
              {seedMutation.isPending ? (isEs ? 'Cargando…' : 'Loading…') : (isEs ? 'Cargar plantilla estándar' : 'Load standard template')}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="min-h-[44px] px-5 rounded-full border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800]"
            >
              {isEs ? 'Agregar uno' : 'Add one'}
            </button>
          </div>}
        </div>
      )}

      {/* Filters */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <label className="min-w-0">
            <span className="block text-[11px] font-[800] text-[#9E9890] mb-1">{isEs ? 'Estado' : 'Status'}</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ALL' | PunchStatus)}
              className="w-full min-h-[44px] border border-[#ECEAE4] rounded-xl px-3 text-[14px] bg-white"
              data-testid="punch-filter-status"
            >
              <option value="ALL">{isEs ? 'Todos' : 'All'}</option>
              {PUNCH_STATUSES.map((s) => <option key={s} value={s}>{PUNCH_STATUS_LABELS[s][lang]}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="block text-[11px] font-[800] text-[#9E9890] mb-1">{isEs ? 'Área' : 'Area'}</span>
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value as 'ALL' | PunchArea)}
              className="w-full min-h-[44px] border border-[#ECEAE4] rounded-xl px-3 text-[14px] bg-white"
              data-testid="punch-filter-area"
            >
              <option value="ALL">{isEs ? 'Todas' : 'All'}</option>
              {areasPresent.map((a) => <option key={a} value={a}>{PUNCH_AREA_LABELS[a][lang]}</option>)}
            </select>
          </label>
        </div>
      )}

      {items.length > 0 && filtered.length === 0 && (
        <p className="text-center text-[13px] text-[#9E9890] py-6">{isEs ? 'Ningún pendiente coincide con el filtro.' : 'No items match the filter.'}</p>
      )}

      {/* Groups */}
      {groups.map((group) => {
        const gp = punchProgress(group.items)
        return (
          <section key={group.area} className="bg-white border border-[#ECEAE4] rounded-2xl overflow-hidden" data-testid={`punch-group-${group.area}`}>
            <header className="flex items-center justify-between gap-2 px-4 py-3 bg-[#FAFAF8] border-b border-[#ECEAE4]">
              <h3 className="text-[13px] font-[900] text-[#1C1A17] uppercase tracking-[0.3px] min-w-0 truncate">{PUNCH_AREA_LABELS[group.area][lang]}</h3>
              <span className="text-[12px] font-[800] text-[#9E9890] shrink-0">{gp.closed}/{gp.total} · {gp.percent}%</span>
            </header>
            <ul className="divide-y divide-[#ECEAE4]">
              {group.items.map((item) => {
                const status: PunchStatus = isPunchStatus(item.status) ? item.status : 'OPEN'
                const st = STATUS_STYLES[status]
                const dueKey = dateOnlyKey(item.dueDate)
                const overdue = !!dueKey && dueKey < todayKey && !isPunchClosed(status)
                const responsible = responsibleOf(item)
                const meta = [
                  item.trade ? displayPunchTrade(item.trade, lang) : null,
                  responsible || null,
                ].filter(Boolean).join(' · ')
                const canAdvance = status !== 'VERIFIED'
                if (editingId === item.id) {
                  return (
                    <li key={item.id} className="p-3">
                      <ItemForm
                        isEs={isEs}
                        lang={lang}
                        initial={item}
                        contacts={contacts}
                        contactsFailed={contactsQuery.isError}
                        projectId={projectId}
                        isDemo={isDemo}
                        photoToken={photoToken}
                        busy={updateMutation.isPending || deleteMutation.isPending}
                        onCancel={() => setEditingId(null)}
                        onSubmit={(payload) => updateMutation.mutate({ id: item.id, patch: payload })}
                        onDelete={() => deleteMutation.mutate(item.id)}
                        onValidationError={(code) => setActionError(code)}
                      />
                    </li>
                  )
                }
                return (
                  <li key={item.id} className="flex items-start gap-3 px-3 py-3" data-testid="punch-item">
                    <button
                      type="button"
                      disabled={!canWrite || !canAdvance || busy}
                      onClick={() => updateMutation.mutate({ id: item.id, patch: { status: nextPunchStatus(status) } })}
                      title={canAdvance
                        ? (isEs ? `Tocar para pasar a: ${PUNCH_STATUS_LABELS[nextPunchStatus(status)].es}` : `Tap to move to: ${PUNCH_STATUS_LABELS[nextPunchStatus(status)].en}`)
                        : (isEs ? 'Verificado — para reabrir, edita el pendiente' : 'Verified — edit the item to reopen it')}
                      className="shrink-0 min-h-[44px] min-w-[44px] max-w-[112px] px-2.5 rounded-xl border text-[11px] font-[900] leading-tight text-center disabled:cursor-default"
                      style={{ background: st.bg, color: st.text, borderColor: st.border }}
                      data-testid="punch-status-chip"
                    >
                      {PUNCH_STATUS_LABELS[status][lang]}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[14px] leading-snug break-words ${isPunchClosed(status) ? 'text-[#9E9890] line-through decoration-[#9E9890]/50' : 'text-[#1C1A17]'}`}>
                        {displayPunchDescription(item, lang)}
                      </p>
                      {(meta || item.dueDate) && (
                        <p className="mt-1 text-[12px] text-[#9E9890] break-words">
                          {meta}
                          {meta && item.dueDate ? ' · ' : ''}
                          {item.dueDate && (
                            <span className={overdue ? 'text-[#B91C1C] font-[800]' : ''}>
                              {(isEs ? 'Vence ' : 'Due ') + formatDateOnly(item.dueDate, isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })}
                              {overdue ? (isEs ? ' (vencido)' : ' (overdue)') : ''}
                            </span>
                          )}
                        </p>
                      )}
                      {item.notes && <p className="mt-1 text-[12px] text-[#6B665F] break-words">{item.notes}</p>}
                      {item.photoPath && (
                        <img
                          src={photoUrl(item.photoPath, photoToken)}
                          alt={isEs ? 'Foto del pendiente' : 'Item photo'}
                          className="mt-2 h-20 w-28 object-cover rounded-lg border border-[#ECEAE4]"
                          loading="lazy"
                        />
                      )}
                    </div>
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => { setEditingId(item.id); setShowAdd(false) }}
                        aria-label={isEs ? 'Editar pendiente' : 'Edit item'}
                        className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-[#6B665F] hover:bg-[#F2F0EB]"
                        data-testid="punch-edit"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

/** Progress-photo URL (served by the owner-scoped /api/uploads/* route). */
function photoUrl(storagePath: string, token: string | null): string {
  if (/^https?:\/\//i.test(storagePath)) return storagePath
  const base = apiUrl(`/api/uploads/${storagePath.replace(/^\/+/, '')}`)
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

// ── FORM ──────────────────────────────────────────

const OTHER_CONTACT = '__free_text__'

function ItemForm({
  isEs, lang, initial, contacts, contactsFailed, projectId, isDemo, photoToken, busy, onCancel, onSubmit, onDelete, onValidationError,
}: {
  isEs: boolean
  lang: 'es' | 'en'
  initial?: PunchItem
  contacts: Contact[]
  contactsFailed: boolean
  projectId?: string
  isDemo: boolean
  photoToken: string | null
  busy: boolean
  onCancel: () => void
  onSubmit: (payload: ItemPayload) => void
  onDelete?: () => void
  onValidationError: (code: string) => void
}) {
  const { getToken } = useAuth()
  const [area, setArea] = useState<PunchArea>(((PUNCH_AREAS as readonly string[]).includes(initial?.area ?? '') ? initial!.area : 'OTHER') as PunchArea)
  const [description, setDescription] = useState(initial ? displayPunchDescription(initial, lang) : '')
  const [trade, setTrade] = useState(initial?.trade ?? '')
  const [contactId, setContactId] = useState<string>(initial?.responsibleContactId ?? (initial?.responsibleName ? OTHER_CONTACT : ''))
  const [freeName, setFreeName] = useState(initial?.responsibleName ?? '')
  const [status, setStatus] = useState<PunchStatus>(isPunchStatus(initial?.status) ? initial!.status as PunchStatus : 'OPEN')
  const [dueDate, setDueDate] = useState(toDateInputValue(initial?.dueDate))
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [photoChoice, setPhotoChoice] = useState<string | null | undefined>(undefined) // undefined = unchanged
  const [pickingPhoto, setPickingPhoto] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const photosQuery = useQuery<Photo[]>({
    queryKey: ['photos', projectId, 'punch-picker'],
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/photos`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
      if (!r.ok) throw new Error('load')
      const body = await r.json()
      return Array.isArray(body) ? body : []
    },
    enabled: pickingPhoto && !isDemo && !!projectId,
    retry: false,
  })

  const tradeKnown = Object.keys(PUNCH_TRADES)
  const currentPhotoPath = photoChoice === undefined
    ? (initial?.photoPath ?? null)
    : photoChoice === null ? null : (photosQuery.data?.find((p) => p.id === photoChoice)?.storagePath ?? null)

  const submit = () => {
    const desc = description.trim()
    if (!desc) { onValidationError('description_required'); return }
    const payload: ItemPayload = {
      area,
      description: desc,
      trade: trade.trim() || null,
      responsibleContactId: contactId && contactId !== OTHER_CONTACT ? contactId : null,
      responsibleName: contactId === OTHER_CONTACT ? (freeName.trim() || null) : null,
      status,
      dueDate: dueDate || null,
      notes: notes.trim() || null,
    }
    if (photoChoice !== undefined) payload.progressPhotoId = photoChoice
    onSubmit(payload)
  }

  const field = 'w-full min-h-[44px] border border-[#ECEAE4] rounded-xl px-3 text-[14px] bg-white'
  const label = 'block text-[11px] font-[800] text-[#9E9890] mb-1'

  return (
    <div className="bg-white border border-[#06539B]/30 rounded-2xl p-4 space-y-3 w-full min-w-0" data-testid="punch-form">
      <div className="flex items-center justify-between">
        <h4 className="text-[14px] font-[900] text-[#1C1A17]">{initial ? (isEs ? 'Editar pendiente' : 'Edit item') : (isEs ? 'Nuevo pendiente' : 'New item')}</h4>
        <button type="button" onClick={onCancel} aria-label={isEs ? 'Cancelar' : 'Cancel'} className="min-h-[44px] min-w-[44px] -mr-2 flex items-center justify-center text-[#9E9890]">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="min-w-0">
          <span className={label}>{isEs ? 'Área' : 'Area'}</span>
          <select value={area} onChange={(e) => setArea(e.target.value as PunchArea)} className={field}>
            {PUNCH_AREAS.map((a) => <option key={a} value={a}>{PUNCH_AREA_LABELS[a][lang]}</option>)}
          </select>
        </label>
        <label className="min-w-0">
          <span className={label}>{isEs ? 'Estado' : 'Status'}</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as PunchStatus)} className={field} data-testid="punch-form-status">
            {PUNCH_STATUSES.map((s) => <option key={s} value={s}>{PUNCH_STATUS_LABELS[s][lang]}</option>)}
          </select>
        </label>
      </div>

      <label className="block min-w-0">
        <span className={label}>{isEs ? 'Descripción' : 'Description'}</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={2000}
          className="w-full border border-[#ECEAE4] rounded-xl px-3 py-2.5 text-[14px] bg-white"
          placeholder={isEs ? 'Ej. Retocar pintura junto a la puerta del baño' : 'e.g. Touch up paint by the bathroom door'}
        />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="min-w-0">
          <span className={label}>{isEs ? 'Oficio' : 'Trade'}</span>
          <input
            list="punch-trades"
            value={tradeKnown.includes(trade) ? displayPunchTrade(trade, lang) : trade}
            onChange={(e) => {
              const v = e.target.value
              const hit = tradeKnown.find((k) => PUNCH_TRADES[k][lang] === v)
              setTrade(hit ?? v)
            }}
            maxLength={100}
            className={field}
          />
          <datalist id="punch-trades">
            {tradeKnown.map((k) => <option key={k} value={PUNCH_TRADES[k][lang]} />)}
          </datalist>
        </label>
        <label className="min-w-0">
          <span className={label}>{isEs ? 'Fecha límite' : 'Due date'}</span>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={field} />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="min-w-0">
          <span className={label}>{isEs ? 'Responsable' : 'Responsible'}</span>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className={field} data-testid="punch-form-contact">
            <option value="">{isEs ? 'Sin asignar' : 'Unassigned'}</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.name} (${c.company})` : c.name}</option>)}
            <option value={OTHER_CONTACT}>{isEs ? 'Otro (escribir nombre)' : 'Other (type a name)'}</option>
          </select>
          {contactsFailed && (
            <span className="block mt-1 text-[11px] text-[#B45309]">{isEs ? 'No se pudieron cargar los contactos; puedes escribir el nombre.' : 'Could not load contacts; you can type a name.'}</span>
          )}
        </label>
        {contactId === OTHER_CONTACT && (
          <label className="min-w-0">
            <span className={label}>{isEs ? 'Nombre del responsable' : 'Responsible name'}</span>
            <input value={freeName} onChange={(e) => setFreeName(e.target.value)} maxLength={200} className={field} />
          </label>
        )}
      </div>

      <label className="block min-w-0">
        <span className={label}>{isEs ? 'Notas' : 'Notes'}</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} className="w-full border border-[#ECEAE4] rounded-xl px-3 py-2.5 text-[14px] bg-white" />
      </label>

      {/* Photo: attach an EXISTING progress photo of this project */}
      {!isDemo && (
        <div className="min-w-0">
          <span className={label}>{isEs ? 'Foto (de Fotos de avance)' : 'Photo (from Progress Photos)'}</span>
          <div className="flex flex-wrap items-center gap-2">
            {currentPhotoPath && (
              <img src={photoUrl(currentPhotoPath, photoToken)} alt="" className="h-16 w-20 object-cover rounded-lg border border-[#ECEAE4]" />
            )}
            <button type="button" onClick={() => setPickingPhoto((v) => !v)} className="min-h-[44px] px-4 rounded-full border border-[#ECEAE4] text-[13px] font-[800] flex items-center gap-1.5">
              <Camera size={14} /> {currentPhotoPath ? (isEs ? 'Cambiar foto' : 'Change photo') : (isEs ? 'Adjuntar foto' : 'Attach photo')}
            </button>
            {currentPhotoPath && (
              <button type="button" onClick={() => { setPhotoChoice(null); setPickingPhoto(false) }} className="min-h-[44px] px-4 rounded-full text-[13px] font-[800] text-[#B91C1C]">
                {isEs ? 'Quitar foto' : 'Remove photo'}
              </button>
            )}
          </div>
          {pickingPhoto && (
            <div className="mt-2">
              {photosQuery.isLoading && <p className="text-[12px] text-[#9E9890]">{isEs ? 'Cargando fotos…' : 'Loading photos…'}</p>}
              {photosQuery.isError && <p role="alert" className="text-[12px] text-[#B91C1C]">{isEs ? 'No se pudieron cargar las fotos.' : 'Could not load photos.'}</p>}
              {photosQuery.isSuccess && photosQuery.data.length === 0 && (
                <p className="text-[12px] text-[#9E9890]">{isEs ? 'Este proyecto aún no tiene fotos. Súbelas en Fotos de avance.' : 'No photos in this project yet. Upload them in Progress Photos.'}</p>
              )}
              {photosQuery.isSuccess && photosQuery.data.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-56 overflow-y-auto">
                  {photosQuery.data.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setPhotoChoice(p.id); setPickingPhoto(false) }}
                      className={`aspect-square rounded-lg overflow-hidden border-2 ${photoChoice === p.id ? 'border-[#06539B]' : 'border-transparent'}`}
                      aria-label={p.description || (isEs ? 'Foto de avance' : 'Progress photo')}
                    >
                      <img src={photoUrl(p.thumbnailPath || p.storagePath, photoToken)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button type="button" onClick={submit} disabled={busy} className="min-h-[44px] px-5 rounded-full bg-[#06539B] text-white text-[13px] font-[800] disabled:opacity-60" data-testid="punch-form-save">
          {busy ? (isEs ? 'Guardando…' : 'Saving…') : (isEs ? 'Guardar' : 'Save')}
        </button>
        <button type="button" onClick={onCancel} className="min-h-[44px] px-5 rounded-full border border-[#ECEAE4] text-[13px] font-[800] text-[#1C1A17]">
          {isEs ? 'Cancelar' : 'Cancel'}
        </button>
        {onDelete && !confirmDelete && (
          <button type="button" onClick={() => setConfirmDelete(true)} className="ml-auto min-h-[44px] px-4 rounded-full text-[13px] font-[800] text-[#B91C1C] flex items-center gap-1.5" data-testid="punch-delete">
            <Trash2 size={14} /> {isEs ? 'Eliminar' : 'Delete'}
          </button>
        )}
      </div>
      {onDelete && confirmDelete && (
        <div className="flex flex-wrap items-center gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-3 py-2" data-testid="punch-delete-confirm">
          <span className="flex-1 min-w-0 text-[13px] font-[700] text-[#B91C1C]">{isEs ? '¿Eliminar este pendiente? No se puede deshacer.' : 'Delete this item? This cannot be undone.'}</span>
          <button type="button" onClick={onDelete} disabled={busy} className="min-h-[44px] px-4 rounded-full bg-[#B91C1C] text-white text-[13px] font-[800] disabled:opacity-60">
            {isEs ? 'Sí, eliminar' : 'Yes, delete'}
          </button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="min-h-[44px] px-4 rounded-full border border-[#FECACA] text-[13px] font-[800] text-[#B91C1C]">
            {isEs ? 'No' : 'No'}
          </button>
        </div>
      )}
    </div>
  )
}
